// Sign in, or create an account, with a six-digit code sent by email. Two
// steps on one page: the address, then the code. Signing in merges this
// device's progress with the account's, so nothing is lost either way.
//   ?next=/path   where to go afterwards (same-site paths only)
//   ?why=start    asked before the first lesson: offers "Not now" as well
import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { local, setLocal } from '../lib/store.js';
import { syncState } from '../lib/sync.js';
import { back, navigate } from '../lib/router.js';
import { loadChapter, skyAt } from '../lib/content.js';
import { dawnScene } from '../components/dawn.js';
import { brandMark } from '../components/chrome.js';
import { toast } from '../components/toast.js';
import { signInSteps, bindSignInSteps } from '../components/signin-form.js';
import { safeNext } from '../../shared/auth.js';

const SKY = 0.34;

// "No effort on this path is ever lost": the verse fills in once chapter 2 is
// loaded, and the page never waits for it.
const quote = (where) => html`<figure class="signin__quote signin__quote--${where}" data-quote hidden>
  ${icon('quote', { size: 22, className: 'signin__quote-mark' })}
  <blockquote><p></p></blockquote>
  <figcaption>Bhagavad Gita 2.40</figcaption>
</figure>`;

function copyFor(why, adding) {
  if (adding) {
    return {
      eyebrow: 'One more step',
      title: 'Add your email',
      lede: 'Signing in now works with a code sent to your email. Add yours to keep this account and everything in it.',
      verify: 'Add email',
    };
  }
  if (why === 'start') {
    return {
      eyebrow: 'Before you begin',
      title: 'Save your progress as you go',
      lede: "Sign in with your email and every lesson you finish is kept safe, ready on any phone or computer. We'll send you a 6-digit code; there's no password.",
    };
  }
  if (why === 'save') {
    return {
      eyebrow: 'Save your progress',
      title: 'Keep your place on every device',
      lede: "Your progress is only on this device right now. Enter your email and we'll send you a 6-digit code to save it to your account.",
    };
  }
  return {};
}

export default async function signin(params, r) {
  const next = safeNext(r.query && r.query.get('next'), '/');
  const why = r.query && r.query.get('why');
  const user = syncState().user || local().user;
  if (user && user.email) return { redirect: next === '/' ? '/settings' : next };
  // Signed in from before email sign-in existed: adding an email keeps the account.
  const adding = !!user;
  const copy = copyFor(why, adding);
  const start = why === 'start' && !adding;
  return {
    title: adding ? 'Add your email' : start ? 'Save your progress' : 'Sign in',
    focus: true,
    tint: skyAt(SKY * 0.82),
    html: html`<div class="signin" data-step="email">
      <div class="signin__sky">
        ${dawnScene(SKY, { className: 'signin__dawn' })}
        ${quote('sky')}
      </div>
      <button type="button" class="icon-btn signin__close" data-back="${start ? '/' : next}" aria-label="Close">${icon('close', { size: 22 })}</button>
      <div class="signin__panel">
        <div class="signin__inner">
          <div class="signin__brand">${brandMark(34)}<span class="brand__word">Bhagavad Gita</span></div>
          ${signInSteps({ id: 'si', copy })}
          ${start ? html`<p class="signin__skip"><button type="button" class="btn btn--ghost btn--small" data-skip>Not now, start the first lesson</button></p>` : ''}
          ${start ? '' : html`<ul class="signin__points" data-points>
            <li>${icon('cloud', { size: 18 })}<span>Everything you have done on this device comes with you.</span></li>
            <li>${icon('shield', { size: 18 })}<span>Your email is only used to sign you in. No newsletters, ever.</span></li>
          </ul>`}
        </div>
        ${quote('foot')}
      </div>
    </div>`,
    mount(root) {
      const page = root.querySelector('.signin');
      const titleFor = (step) => (step === 'code' ? 'Enter your code' : adding ? 'Add your email' : start ? 'Save your progress' : 'Sign in');
      const stop = bindSignInSteps(root, {
        onStep(step) {
          page.dataset.step = step;
          document.title = `${titleFor(step)} · Bhagavad Gita`;
          const points = root.querySelector('[data-points]');
          if (points) points.hidden = step !== 'email';
          const skip = root.querySelector('.signin__skip');
          if (skip) skip.hidden = step !== 'email';
        },
        onSignedIn(res) {
          toast(adding
            ? 'Email added. Sign in with it on any device.'
            : res.created ? 'Your account is ready. Your progress now syncs.' : 'Signed in. Your progress is synced.');
          // Asked on the way into the first lesson: carry on into it.
          if (start) navigate(next, { replace: true });
          else back(next);
        },
      });

      const skip = root.querySelector('[data-skip]');
      if (skip) {
        skip.addEventListener('click', () => {
          setLocal({ welcomed: true });
          navigate(next, { replace: true });
        });
      }

      const onKey = (e) => {
        if (e.key === 'Escape' && !document.querySelector('dialog[open]')) back(start ? '/' : next);
      };
      document.addEventListener('keydown', onKey);

      loadChapter(2).then((c) => {
        const v = c && c.verseMap && c.verseMap.get(40);
        if (!v || !page.isConnected) return;
        for (const f of root.querySelectorAll('[data-quote]')) {
          f.querySelector('p').textContent = v.translation;
          f.hidden = false;
        }
      }).catch(() => {});

      return () => {
        stop();
        document.removeEventListener('keydown', onKey);
      };
    },
  };
}
